import fetch from "unfetch";

const dedupe = arr => arr.filter((x, i) => arr.indexOf(x) === i);

export const getUniqueScopes = (...scopes) => {
    const scopeString = scopes.filter(Boolean).join();
    return dedupe(scopeString.replace(/\s/g, ",").split(","))
        .join(" ")
        .trim();
};

export const parseQueryResult = (queryString) => {
    if (queryString.indexOf("#") > -1) {
        queryString = queryString.substr(0, queryString.indexOf("#"));
    }

    const queryParams = queryString.split("&");

    const parsedQuery = {};
    queryParams.forEach(qp => {
        const [key, val] = qp.split("=", 2);
        parsedQuery[key] = decodeURIComponent(val);
    });

    return {
        ...parsedQuery,
        expires_in: parseInt(parsedQuery.expires_in)
    };
};

export const createRandomString = () => {
    const charset =
        "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.";
    let random = "";
    const randomValues = Array.from(
        // always use a number which can divided by 8,
        // otherwise '=' at the end can cause problems
        getCrypto().getRandomValues(new Uint8Array(48))
    );
    randomValues.forEach(v => (random += charset[v % charset.length]));
    return random;
};

export const encodeState = (state) => btoa(state);
// export const decodeState = (state) => atob(state);

export const createQueryParams = (params) => {
    return Object.keys(params)
        .filter(k => typeof params[k] !== "undefined")
        .map(k => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
        .join("&");
};

export const sha256 = (s) => {
    const digestOp = getCryptoSubtle().digest(
        {name: "SHA-256"},
        new TextEncoder().encode(s)
    );

    // msCrypto (IE11) uses the old spec, which is not Promise based
    // https://msdn.microsoft.com/en-us/expression/dn904640(v=vs.71)
    // Instead of returning a promise, it returns a CryptoOperation
    // with a result property in it.
    // As a result, the various events need to be handled in the event that we're
    // working in IE11 (hence the msCrypto check). These events just call resolve
    // or reject depending on their intention.
    if ((window).msCrypto) {
        return new Promise((res, rej) => {
            digestOp.oncomplete = e => {
                res(e.target.result);
            };

            digestOp.onerror = (e) => {
                rej(e.error);
            };

            digestOp.onabort = () => {
                rej("The digest operation was aborted");
            };
        });
    }

    return digestOp;
};

const urlEncodeB64 = (input) => {
    const b64Chars = {"+": "-", "/": "_", "=": ""};
    return input.replace(/[+/=]/g, (m) => b64Chars[m]);
};

// https://stackoverflow.com/questions/30106476/
const decodeB64 = input =>
    decodeURIComponent(
        atob(input)
            .split("")
            .map(c => {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
    );

export const urlDecodeB64 = (input) =>
    decodeB64(input.replace(/_/g, "/").replace(/-/g, "+"));

export const bufferToBase64UrlEncoded = input => {
    const ie11SafeInput = new Uint8Array(input);
    return urlEncodeB64(
        window.btoa(String.fromCharCode(...Array.from(ie11SafeInput)))
    );
};

const getJSON = async (url, options) => {
    const response = await fetch(url, options);
    const {error, error_description, ...success} = await response.json();
    if (!response.ok) {
        const errorMessage =
            error_description || `HTTP error. Unable to fetch ${url}`;
        const e = new Error(errorMessage);
        e.error = error || "request_error";
        e.error_description = errorMessage;
        throw e;
    }
    return success;
};

export const oauthToken = async ({tokenEndpoint, ...options}) => {
    const params = new URLSearchParams();
    for (const key in options) {
        params.append(key, options[key]);
    }
    return await getJSON(tokenEndpoint, {
        method: "POST",
        body: params,
        headers: {
            "Content-type": "application/x-www-form-urlencoded"
        }
    });
};

export const getCrypto = () => {
    //ie 11.x uses msCrypto
    return (window.crypto || (window).msCrypto);
};

export const getCryptoSubtle = () => {
    const crypto = getCrypto();
    //safari 10.x uses webkitSubtle
    return crypto.subtle || (crypto).webkitSubtle;
};

export const validateCrypto = () => {
    if (!getCrypto()) {
        throw new Error(
            "For security reasons, `window.crypto` is required to run this library."
        );
    }
    if (typeof getCryptoSubtle() === "undefined") {
        throw new Error(`
      this library must run on a secure origin.
      See https://github.com/auth0/auth0-spa-js/blob/master/FAQ.md#why-do-i-get-auth0-spa-js-must-run-on-a-secure-origin 
      for more information.
    `);
    }
};

export function isCognito(issuer) {
    return Boolean(issuer.includes("cognito"));
}